
import appCan from '@@/bits/app_can'
import { supportsRecording, supportsVideoRecording } from '@@/bits/av_recorder'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { recordWhitelistedClick } from '@@/bits/pepin'
import { PiniaMapAction, PiniaMapState } from '@@/bits/pinia'
import { Action, Component, Getter, State, Vue, defineAsyncComponent, namespace } from '@@/bits/vue'
import { SearchFilters } from '@@/bits/web_search'
import { UploadIntent } from '@@/native_bridge/actions'
import { useComposerModalStore } from '@@/pinia/composer_modal'
import type { ContentPickerSearchMode } from '@@/pinia/content_picker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import type { Cid, Post, User } from '@@/types'
import type { ContentPickerSourceMenuListItem } from '@@/vuecomponents/ContentPickerSourceMenuList.vue'
import { ContentPickerSource } from '@@/vuexstore/modules/content_picker'

@Component({
  components: {
    ContentPicker: defineAsyncComponent(() => import('@@/vuecomponents/ContentPicker.vue')),
    SurfaceContentPickerOld: defineAsyncComponent(() => import('@@/vuecomponents/content_picker_old.vue')),
    SurfaceAddPostButton: defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPostButton.vue')),
    SurfaceSectionPicker: defineAsyncComponent(() => import('@@/vuecomponents/surface_section_picker.vue')),
  },
})
export default class SurfaceAddPost extends Vue {
  recordWhitelistedClick = recordWhitelistedClick
  cameraSupport = !device.zoomApp
  audioSupport = !device.zoomApp && supportsRecording
  isAppUsing = isAppUsing
  UploadIntent = UploadIntent
  isChrome = device.chrome
  isFirefox = device.firefox
  isMobile = device.mobile

  @State user!: User
  @State isOnline!: boolean
  @State isEmbedded!: boolean
  @State isReadOnly!: boolean

  @Getter canIWrite!: boolean
  @Getter dir!: 'ltr' | 'rtl'
  @Getter isApp!: boolean
  @Getter isDesktop!: boolean
  @Getter isPhone!: boolean
  @Getter isShelf!: boolean
  @Getter isTable!: boolean
  @Getter canUseSections!: boolean
  @Getter isSectioned!: boolean
  @Getter isMobileLayout!: boolean

  @Action showOverFileSizeModal!: (payload: { file: any }) => void
  @Action showExtensionRequired

  @PiniaMapState(useComposerModalStore) isDocked!: boolean
  @namespace('contentPicker').Getter xContentSourceMenu!: boolean
  @namespace('contentPicker').Getter activeContentPickerPanel!: ContentPickerSource
  @PiniaMapState(useSurfaceDraftsStore) isActiveDraftNewPost!: boolean
  @namespace('post').Getter pendingPosts!: Post[]
  @namespace('post').Getter postBeingEditedCid!: Cid
  @namespace('post').Action saveContent
  @namespace('post').Action saveAttachmentCaption
  @PiniaMapState(useSurfaceStore) isSubmissionRequest!: boolean

  @PiniaMapAction(useSurfacePostPropertiesStore) isAttachmentTypeEnabled!: (type: ContentPickerSource) => boolean

  @PiniaMapState(useSurfacePermissionsStore) amIRegistered!: boolean

  @PiniaMapState(useSurfaceDraftsStore) activeDraft!: ReturnType<typeof useSurfaceDraftsStore>['activeDraft']
  @PiniaMapState(useSurfaceDraftsStore) isAnyDraftActive!: ReturnType<typeof useSurfaceDraftsStore>['isAnyDraftActive']

  @PiniaMapAction(useSurfaceDraftsStore) uploadFileActiveDraft!: ReturnType<
    typeof useSurfaceDraftsStore
  >['uploadFileActiveDraft']

  @PiniaMapAction(useSurfaceDraftsStore) updateActiveDraft!: ReturnType<
    typeof useSurfaceDraftsStore
  >['updateActiveDraft']

  @PiniaMapState(useSurfaceContentPickerStore) defaultSearchMode!: SearchFilters
  @PiniaMapState(useSurfaceContentPickerStore) currentSearchModes!: ContentPickerSearchMode[]
  @PiniaMapState(useSurfaceContentPickerStore) searchSuggestions!: ReturnType<
    typeof useSurfaceContentPickerStore
  >['searchSuggestions']

  onUploadDone(url: string): void {
    this.saveContent({ content: { link: url } })
  }

  onUploadReject(file): void {
    this.showOverFileSizeModal({ file })
  }

  setCaption(caption): void {
    if (!caption) {
      return
    }
    this.saveAttachmentCaption({ caption })
  }

  get videoSupport(): boolean {
    return !device.zoomApp && (supportsVideoRecording || this.isMobile)
  }

  get screenshareSupport(): boolean {
    return !device.zoomApp && supportsVideoRecording && !this.isMobile && (this.isChrome || this.isFirefox)
  }

  get canShowAddPost(): boolean {
    const isAppWithNewPosting = this.isApp && appCan('newPostModal')
    const isActiveDraftDocked = this.isDocked && this.isActiveDraftNewPost && this.isDesktop && !this.isPhone
    const isAPostBeingEdited = !!this.postBeingEditedCid
    return (
      !isAppWithNewPosting &&
      (this.isEmbedded || !this.isMobileLayout) &&
      this.canIWrite &&
      !this.isReadOnly &&
      (!isAPostBeingEdited || isActiveDraftDocked)
    )
  }

  get xAddPostButton(): boolean {
    return this.canShowAddPost && !this.isTable && !this.isShelf && !this.isSubmissionRequest
  }

  get xContentPicker(): boolean {
    return this.xContentSourceMenu === true || this.activeContentPickerPanel !== null
  }

  get xSectionPicker(): boolean {
    if (this.canUseSections && this.isSectioned) {
      const pendingPostsWithoutSections = this.pendingPosts.filter((p: Post) => !p.wall_section_id)
      return pendingPostsWithoutSections.length > 0
    }
    return false
  }

  get xSearchBar(): boolean {
    return this.contentSources
      .flat()
      .filter((source) => source.visible)
      .some((source) =>
        [
          ContentPickerSource.ImageSearch,
          ContentPickerSource.GifSearch,
          ContentPickerSource.VideoSearch,
          ContentPickerSource.AudioSearch,
          ContentPickerSource.WebBrowser,

          // We are also using the search bar to handle pasted link
          // or I can't draw prompts -> need to show it when these
          // sources are enabled
          ContentPickerSource.LinkInput,
          ContentPickerSource.AiImageSearch,
        ].includes(source.type as ContentPickerSource),
      )
  }

  get contentSources(): ContentPickerSourceMenuListItem[][] {
    const section1: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.PadletBrowser,
        text: __('Padlet'),
        ariaLabel: __('Attach another padlet'),
        icon: isAppUsing('contentPickerV2') ? 'padlet_outline' : 'padlet',
        // There’s an issue where exporting the correct stroke width for the Padlet logo specifically breaks the svg, so the exported value is thinner than it should be, and we manually override it here
        iconStrokeWidth: isAppUsing('contentPickerV2') ? 0.5 : undefined,
        color: 'purple',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.PadletBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.PadletBrowser,
        testId: 'attachAnotherPadletButton',
      },
      {
        type: ContentPickerSource.FileBrowser,
        text: __('Upload'),
        ariaLabel: __('Upload a file'),
        icon: isAppUsing('contentPickerV2') ? 'upload_outline' : 'upload',
        color: 'purple',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.FileBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.FileBrowser,
        hiddenInputId: 'content-hidden-file-input',
        testId: 'contentPickerSourceUploadButton',
      },
      {
        type: ContentPickerSource.LinkInput,
        text: __('Link'),
        ariaLabel: __('Attach a link'),
        icon: isAppUsing('contentPickerV2') ? 'link_outline' : 'link',
        color: 'purple',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.LinkInput),
        disabled: false,
        actionType: 'trigger-link-input',
        testId: 'contentPickerSourceLinkButton',
      },
    ]

    const section2: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.PhotoBooth,
        text: __('Camera'),
        ariaLabel: __('Take a photo'),
        icon: isAppUsing('contentPickerV2') ? 'camera_outline' : 'camera',
        color: 'blue',
        visible: this.cameraSupport && this.isAttachmentTypeEnabled(ContentPickerSource.PhotoBooth),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.PhotoBooth,
        hiddenInputId: 'content-hidden-image-input',
        testId: 'contentPickerSourceCameraButton',
      },
      {
        type: ContentPickerSource.VideoRecorder,
        text: __('Video recorder'),
        ariaLabel: __('Record a video'),
        icon: isAppUsing('contentPickerV2') ? 'video_outline' : 'video',
        color: 'blue',
        visible: this.videoSupport && this.isAttachmentTypeEnabled(ContentPickerSource.VideoRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.VideoRecorder,
        hiddenInputId: 'content-hidden-video-input',
        testId: 'contentPickerSourceVideoButton',
      },
      {
        type: ContentPickerSource.AudioRecorder,
        text: __('Audio recorder'),
        ariaLabel: __('Record an audio'),
        icon: isAppUsing('contentPickerV2') ? 'microphone_outline' : 'microphone',
        color: 'blue',
        visible: this.audioSupport && this.isAttachmentTypeEnabled(ContentPickerSource.AudioRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.AudioRecorder,
        testId: 'contentPickerSourceAudioButton',
      },
      {
        type: ContentPickerSource.ScreenshareRecorder,
        text: __('Screen recorder'),
        ariaLabel: __('Record your screen'),
        icon: isAppUsing('contentPickerV2') ? 'screen_recorder_outline' : 'screen_recorder',
        color: 'blue',
        visible:
          !device.tablet &&
          !device.phone &&
          this.screenshareSupport &&
          this.isAttachmentTypeEnabled(ContentPickerSource.ScreenshareRecorder),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.ScreenshareRecorder,
        testId: 'contentPickerSourceScreenRecordButton',
      },
      {
        type: ContentPickerSource.DrawingCanvas,
        text: __('Draw'),
        ariaLabel: __('Draw something'),
        icon: isAppUsing('contentPickerV2') ? 'draw_outline' : 'draw',
        color: 'blue',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.DrawingCanvas),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.DrawingCanvas,
        testId: 'contentPickerSourceDrawButton',
      },
    ]
    const section3: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.Poll,
        text: __('Poll'),
        ariaLabel: __('Make a poll'),
        icon: isAppUsing('contentPickerV2') ? 'poll_outline' : 'poll',
        color: 'green',
        isNew: true,
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.Poll),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.Poll,
        testId: 'makeAPollButton',
      },
      {
        type: ContentPickerSource.GoogleDrive,
        text: __('Google Drive'),
        ariaLabel: __('Upload from Google Drive'),
        icon: isAppUsing('contentPickerV2') ? 'google_drive_outline' : 'google_drive_fill',
        color: 'green',
        isNew: true,
        visible:
          isAppUsing('googleDrive') &&
          this.amIRegistered &&
          this.isAttachmentTypeEnabled(ContentPickerSource.GoogleDrive),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.GoogleDrive,
        testId: 'contentPickerSourceGoogleDriveButton',
      },
      {
        type: ContentPickerSource.AiImageSearch,
        text: __("I can't draw"),
        ariaLabel: __('Use AI Art Generator'),
        icon: isAppUsing('contentPickerV2') ? 'sparkles_outline' : 'sparkles',
        color: 'green',
        isNew: true,
        visible: isAppUsing('aiImageSearch') && this.isAttachmentTypeEnabled(ContentPickerSource.AiImageSearch),
        disabled: false,
        actionType: 'trigger-ai-image-search',
        testId: 'contentPickerSourceAiImageSearchButton',
      },
    ]
    const section4: ContentPickerSourceMenuListItem[] = [
      {
        type: ContentPickerSource.ImageSearch,
        text: __('Image search'),
        ariaLabel: __('Search for an image'),
        icon: isAppUsing('contentPickerV2') ? 'image_search_outline' : 'image_search',
        color: 'orange',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.ImageSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Images,
        testId: 'contentPickerSourceImageSearchButton',
      },
      {
        type: ContentPickerSource.GifSearch,
        text: __('GIF'),
        ariaLabel: __('Search for a GIF'),
        icon: 'gif',
        color: 'orange',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.GifSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Gif,
        testId: 'contentPickerSourceGifSearchButton',
      },
      {
        type: ContentPickerSource.VideoSearch,
        text: __('YouTube'),
        ariaLabel: __('Search on YouTube'),
        icon: isAppUsing('contentPickerV2') ? 'youtube_outline' : 'youtube',
        color: 'orange',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.VideoSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Videos,
        testId: 'contentPickerSourceVideoSearchButton',
      },
      {
        type: ContentPickerSource.AudioSearch,
        text: __('Spotify'),
        ariaLabel: __('Search on Spotify'),
        icon: isAppUsing('contentPickerV2') ? 'spotify_outline' : 'spotify',
        color: 'orange',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.AudioSearch),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Audio,
        testId: 'contentPickerSourceAudioSearchButton',
      },
      {
        type: ContentPickerSource.WebBrowser,
        text: __('Web search'),
        ariaLabel: __('Search on the web'),
        icon: isAppUsing('contentPickerV2') ? 'web_search_outline' : 'web_search',
        color: 'orange',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.WebBrowser),
        disabled: false,
        actionType: 'focus-search-bar',
        actionPayload: SearchFilters.Web,
        testId: 'contentPickerSourceWebSearchButton',
      },
      {
        type: ContentPickerSource.MapBrowser,
        text: __('Location'),
        ariaLabel: __('Add a location'),
        icon: isAppUsing('contentPickerV2') ? 'location_outline' : 'location',
        color: 'orange',
        visible: this.isAttachmentTypeEnabled(ContentPickerSource.MapBrowser),
        disabled: false,
        actionType: 'select',
        actionPayload: ContentPickerSource.MapBrowser,
        testId: 'contentPickerSourceLocationSearchButton',
      },
    ]
    return [section1, section2, section3, section4]
  }
}
